import { useCallback, useRef, useState } from 'react';
import { readSession, removeSession, writeSession } from '../util/session-storage';

// Writes at the point of editing, so navigation cannot race a persistence effect.
export const useSessionState = (key, initialValue, options = {}) => {
  const config = useRef({ initialValue, ...options });
  config.current = { initialValue, ...options };
  const initial = () => {
    const fallback = typeof initialValue === 'function' ? initialValue() : initialValue;
    const saved = readSession(key, fallback);
    if (options.restore) return options.restore(saved, fallback);
    if (fallback === null) return saved;
    if (Array.isArray(fallback)) return Array.isArray(saved) ? saved : fallback;
    return saved !== null && typeof saved === typeof fallback ? saved : fallback;
  };
  const [state, setState] = useState(() => ({ key, value: initial() }));
  const current = useRef(state);
  const [stored, setStored] = useState(true);
  if (state.key !== key) {
    const next = { key, value: initial() };
    current.current = next;
    setState(next);
  } else {
    current.current = state;
  }
  const setValue = useCallback(
    (nextValue) => {
      const previous = current.current.value;
      const value = typeof nextValue === 'function' ? nextValue(previous) : nextValue;
      if (Object.is(previous, value)) return;
      const next = { key, value };
      current.current = next;
      const serialized = config.current.serialize ? config.current.serialize(value) : value;
      setStored(writeSession(key, serialized));
      setState(next);
    },
    [key]
  );
  const clear = useCallback(() => {
    removeSession(key);
    const value =
      typeof config.current.initialValue === 'function'
        ? config.current.initialValue()
        : config.current.initialValue;
    current.current = { key, value };
    setState(current.current);
    setStored(true);
  }, [key]);
  return [current.current.value, setValue, clear, stored];
};
